/*
Offline read-cache for the iPad Capacitor app.

The iPad runs the full webapp inside a Capacitor shell. The app shell is
bundled on-device, but the data it needs to render (current user, round member
profiles, the homepage round list) is normally fetched from the server on every
load, so the app is unusable without connectivity.

This module persists those small JSON payloads in localStorage as they are
fetched online, and serves them back when the device is offline so a
previously-used round opens read-only. The confirmed round aggregate has its
own larger IndexedDB cache (see engine/storageAdapters/roundAggregateCache.ts);
this module covers the lighter identity/navigation payloads around it.

The generic helpers here are gated to the iPad Capacitor build: on desktop and
iPhone they are inert (no reads, no writes). The one exception is the
current-user identity entry, whose dedicated `*CachedCurrentUser` helpers below
run on EVERY platform so the app can render immediately from the last known
identity instead of blocking first paint on getCurrentUser (see
useCurrentUser). Sign-out clears it everywhere via clearOfflineCache.
*/

import { getIsOnline } from "@client/hooks/useNetworkStatus";
import { isIPadCapacitor } from "@client/lib/isIPad";
import { z } from "zod";

// Single source of truth for the iPad gate lives in @client/lib/isIPad; re-export
// it here so existing importers of `offlineCache` keep working and all offline
// gating shares one capability check (including the e2e test override).
export { isIPadCapacitor };

/** True when the iPad app is running without connectivity. */
export function isIPadOffline(): boolean {
  return isIPadCapacitor() && !getIsOnline();
}

const KEY_PREFIX = "sprocessOffline:";

function readCache<T>(key: string): T | null {
  if (typeof window === "undefined") return null;
  try {
    // The storage ACCESS is inside the try, not just the parse: privacy
    // settings / embedded contexts can make localStorage itself throw, and a
    // cache read runs on the boot path — a denied read must be a cache miss,
    // never a blank app.
    const raw = window.localStorage.getItem(KEY_PREFIX + key);
    if (raw == null) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeCache<T>(key: string, value: T): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY_PREFIX + key, JSON.stringify(value));
  } catch {
    // Best-effort (quota, private mode). A missing cache just means the round
    // won't open offline; it never breaks the online path.
  }
}

function removeCache(key: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY_PREFIX + key);
  } catch {
    // Storage access denied — then reads fail too, so no stale entry can be
    // served; don't let it break the caller.
  }
}

/**
 * Runs an online fetch, mirroring its result into the offline cache (iPad
 * only). If the fetch fails while the iPad is offline, returns the cached value
 * instead. On desktop/iPhone this is a transparent pass-through to `fetchFn`.
 */
export async function withOfflineCache<T>(
  key: string,
  fetchFn: () => Promise<T>,
): Promise<T> {
  if (!isIPadCapacitor()) {
    return fetchFn();
  }
  try {
    const result = await fetchFn();
    writeCache(key, result);
    return result;
  } catch (error) {
    // Only serve the cache when genuinely offline. An *online* failure (5xx,
    // 403, revoked access) must surface — silently returning stale identity /
    // profiles would mask real errors and could show wrong permissions or
    // members against an otherwise-fresh aggregate.
    if (isIPadOffline()) {
      const cached = readCache<T>(key);
      if (cached != null) {
        return cached;
      }
    }
    throw error;
  }
}

/**
 * Removes every offline identity/navigation cache entry. Called on sign-out so
 * a later offline launch under a different account isn't served the
 * previous user's cached identity / round list / profiles. Not iPad-gated — a
 * no-op anywhere the keys don't exist.
 */
export function clearOfflineCache(): void {
  if (typeof window === "undefined") return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key != null && key.startsWith(KEY_PREFIX)) keys.push(key);
    }
    for (const key of keys) window.localStorage.removeItem(key);
  } catch {
    // Storage access denied — then nothing was ever cached, so there is
    // nothing to clear; don't let it break the surrounding sign-out.
  }
}

/** Reads a cached value without attempting a fetch (iPad only). */
export function readOfflineCache<T>(key: string): T | null {
  if (!isIPadCapacitor()) return null;
  return readCache<T>(key);
}

/** Writes a value to the offline cache (no-op off the iPad Capacitor build). */
export function writeOfflineCacheIfIPad<T>(key: string, value: T): void {
  if (!isIPadCapacitor()) return;
  writeCache(key, value);
}

/**
 * The getCurrentUser payload we trust from the cache. Kept structural (rather
 * than importing the router output type) so the check can't silently widen.
 * `userName: null` is a legitimate not-yet-completed profile; a missing field
 * is not.
 */
const cachedCurrentUserSchema = z.object({
  userId: z.string().min(1),
  email: z.string(),
  userName: z.string().nullable(),
  isSfcAdmin: z.boolean(),
});

export type CachedCurrentUser = z.infer<typeof cachedCurrentUserSchema>;

/**
 * Structural check on a cached getCurrentUser payload before it is trusted
 * for first paint. localStorage outlives deploys, so an entry written by an
 * older build (or corrupted) must be ignored, never crash the boot path.
 */
export function isValidCachedCurrentUser(
  value: unknown,
): value is CachedCurrentUser {
  return cachedCurrentUserSchema.safeParse(value).success;
}

/**
 * Reads the cached current-user identity (ALL platforms, validated). Callers
 * treat this as a render-now placeholder that the live getCurrentUser query
 * confirms or replaces in the background — never as proof of authentication.
 * Returns the original object, not zod's parsed copy: z.object strips unknown
 * keys, which would drop fields a newer getCurrentUser payload carries.
 */
export function readCachedCurrentUser<T extends CachedCurrentUser>(): T | null {
  const cached = readCache<unknown>(OFFLINE_CACHE_KEYS.currentUser);
  return isValidCachedCurrentUser(cached) ? (cached as T) : null;
}

/** Writes the current-user identity cache (ALL platforms, unlike the iPad-gated helpers). */
export function writeCachedCurrentUser<T extends CachedCurrentUser>(
  value: T,
): void {
  writeCache(OFFLINE_CACHE_KEYS.currentUser, value);
}

/** Removes only the current-user identity entry (sign-out uses clearOfflineCache instead). */
export function clearCachedCurrentUser(): void {
  removeCache(OFFLINE_CACHE_KEYS.currentUser);
}

/**
 * Scope of a lead-video hint: one funder-workspace list — a proposal's or a
 * recommender's clip strip, or a meetings rail — as seen by one effective
 * viewer.
 */
export type LeadClipHintScope = {
  roundId: string;
  /**
   * The list's own identity: a proposalId, a recommender id, or a meetings
   * rail's composite scope (see meetingsLeadHintScopeId). Whatever produces it
   * must be shared by the writer and the reader, or the two silently stop
   * agreeing on a key.
   */
  scopeId: string;
  /** The effective (possibly impersonated) viewer the list was rendered for. */
  effectiveViewerUserId: string;
};

/**
 * Reads the videoId this list led with on the last visit (ALL platforms, like
 * the current-user entry). A scheduling hint only: it lets a funder-workspace
 * load start the lead video's signed-media request in parallel with the list
 * request instead of chained after it. The server authorizes the media request
 * itself, so a stale hint at worst warms a media cache entry (keyed by its own
 * videoId) that nobody plays.
 */
export function readLeadClipVideoIdHint(
  scope: LeadClipHintScope,
): string | null {
  const cached = readCache<unknown>(OFFLINE_CACHE_KEYS.leadClipVideoId(scope));
  return typeof cached === "string" && cached.length > 0 ? cached : null;
}

/**
 * Records the list's current lead videoId (ALL platforms). `null` clears the
 * hint, so a list whose videos were removed stops speculatively fetching the
 * old video's media URLs on every subsequent visit.
 */
export function writeLeadClipVideoIdHint(
  scope: LeadClipHintScope,
  videoId: string | null,
): void {
  const key = OFFLINE_CACHE_KEYS.leadClipVideoId(scope);
  if (videoId == null) {
    removeCache(key);
  } else {
    writeCache(key, videoId);
  }
}

export const OFFLINE_CACHE_KEYS = {
  currentUser: "currentUser",
  currentUserId: "currentUserId",
  // Keyed by effective (viewer) user: getHomepageData is anonymized per the
  // effective user (ctx.getUser()), so a different impersonation must read a
  // different entry — prevents serving one user's round list/data to another
  // offline, while keeping each user's homepage available offline (no
  // clear-on-switch needed). Same scoping rationale as userProfiles below.
  homepageData: (effectiveUserId: string) => `homepageData:${effectiveUserId}`,
  // Keyed by effective (viewer) user: getUserProfiles is anonymized per the
  // effective user, so a different impersonation must read a different entry —
  // prevents serving one user's anonymized profiles to another offline, while
  // keeping each user's profiles available offline (no clear-on-switch needed).
  userProfiles: (roundId: string, effectiveUserId: string) =>
    `userProfiles:${roundId}:${effectiveUserId}`,
  // Keyed by effective (viewer) user, same rationale as userProfiles: the
  // clips strip — and so its lead clip — is derived per effective viewer, and
  // a hint must never schedule speculative requests on behalf of a different
  // viewer signing in later on this device.
  leadClipVideoId: (scope: LeadClipHintScope) =>
    `leadClipVideoId:${scope.roundId}:${scope.scopeId}:${scope.effectiveViewerUserId}`,
} as const;

/**
 * Copy for a list surface whose backing query produced NO response (data still
 * nullish once loading ends) — distinct from a response with zero items. The
 * distinction is what keeps empty states honest: "none exist" may only be
 * claimed when the server actually said so. Offline, the truthful message is
 * that the list can't be loaded (SP-2558 showed "No meetings in this round
 * yet" on a round with meetings, hiding the entry points to playback);
 * online, a missing response is a load failure, not an empty collection.
 */
export function unavailableListMessage(listName: string): string {
  return isIPadOffline()
    ? `${listName} can't be loaded offline.`
    : `${listName} couldn't be loaded. Try again in a moment.`;
}
